// Seletor de ONDE o chat processa: botão no topo da barra lateral (variante "rail") e chip no topbar do mobile ("chip"); abre uma lista
// (listbox) com a Community e as pools dedicadas e on-premise associadas à conta, cada uma com tipo, máquinas online e multiplicador de preço.
// Com um escopo só (nada mais a escolher) vira rótulo estático. Setas navegam, Enter escolhe, Esc fecha. O estado vive em scope.tsx.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Globe, HardDrive, Server } from 'lucide-react';
import type { Scope } from '../api';
import { fmtMult, scopeName, useScope } from '../scope';
import '../styles/scope-picker.css';

const KIND_ICON = { community: Globe, onprem: HardDrive, dedicated: Server } as const;
const COMMUNITY_NOTICE = 'processado em máquinas de terceiros da rede';   // T-1: a Community roda em máquina de outra pessoa
const machines = (n: number) => `${n} ${n === 1 ? 'máquina' : 'máquinas'}`;
const kindText = (s: Scope) => (s.kind === 'community' ? (s.priority ? 'rede · prioritário' : 'rede compartilhada') : s.kind === 'onprem' ? 'pool on-premise' : 'pool dedicada');

/** A frase sob o botão (barra lateral): onde o chat vai rodar. */
function hintOf(s: Scope): string {
  if (!s.available && s.reason) return s.reason;
  return s.kind === 'community' ? COMMUNITY_NOTICE : s.kind === 'onprem' ? `pool on-premise · ${machines(s.machines)}` : `pool dedicada · ${machines(s.machines)}`;
}

export function ScopePicker({ variant = 'rail' }: { variant?: 'rail' | 'chip' }) {
  const uid = useId();
  const { scopes, failed, active, current, select, reload } = useScope();
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);   // opção destacada (índice em `scopes`)
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // clique fora da lista fecha (sem roubar o foco do que foi clicado)
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { const n = e.target as Node; if (!popRef.current?.contains(n) && !btnRef.current?.contains(n)) setOpen(false); };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open]);
  useEffect(() => { if (open) popRef.current?.focus(); }, [open]);
  useEffect(() => { if (open) popRef.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`)?.scrollIntoView({ block: 'nearest' }); }, [open, hi]);

  if (!scopes || !current) return <div className={`sp sp-${variant}`}><div className="sp-skel skel" aria-busy="true" aria-label="Carregando onde processar" /></div>;

  const Icon = KIND_ICON[current.kind], name = scopeName(current), hot = current.multiplier !== 1;
  // só a Community (nenhuma pool associada): sem nada a escolher, o controle é só um rótulo
  const fixed = failed || scopes.length <= 1;

  if (fixed) {
    return (
      <div className={`sp sp-${variant} sp-static`}>
        {variant === 'rail' && <span className="sp-cap">Processar em</span>}
        <div className="sp-box" title={hintOf(current)}>
          <Icon className="sp-ico" /><span className="sp-name">{name}</span><span className={`sp-mult${hot ? ' hot' : ''}`}>{fmtMult(current.multiplier)}</span>
        </div>
        {variant === 'rail' && <span className="sp-hint">{hintOf(current)}</span>}
      </div>
    );
  }

  const show = () => { setHi(Math.max(0, scopes.findIndex((s) => s.id === active))); setOpen(true); void reload(); };
  const close = (focus = true) => { setOpen(false); if (focus) btnRef.current?.focus(); };
  const choose = async (s: Scope | undefined) => { if (!s || !s.available) return; close(); await select(s.id); };

  const onBtnKey = (e: KeyboardEvent<HTMLButtonElement>) => { if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); show(); } };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(scopes.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End') { e.preventDefault(); setHi(scopes.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void choose(scopes[hi]); }
    else if (e.key === 'Tab') close(false);
  };

  return (
    <div className={`sp sp-${variant}`}>
      {variant === 'rail' && <span className="sp-cap">Processar em</span>}
      <div className="sp-anchor">
        <button
          ref={btnRef} type="button" className="sp-btn" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? `${uid}-list` : undefined}
          aria-label="Escolher onde processar" aria-describedby={`${uid}-cur`} title={hintOf(current)} onClick={() => (open ? close() : show())} onKeyDown={onBtnKey}
        >
          <Icon className="sp-ico" /><span className="sp-name" id={`${uid}-cur`}>{name}</span>
          <span className={`sp-mult${hot ? ' hot' : ''}`}>{fmtMult(current.multiplier)}</span><ChevronDown className="sp-chev" />
        </button>

        {open && (
          <div className="sp-pop" id={`${uid}-list`} ref={popRef} tabIndex={-1} role="listbox" aria-label="Onde processar" aria-activedescendant={`${uid}-o${hi}`} onKeyDown={onKey}>
            {scopes.map((s, i) => {
              const I = KIND_ICON[s.kind], sel = s.id === active, off = !s.available;
              return (
                <div
                  key={s.id} id={`${uid}-o${i}`} data-i={i} role="option" aria-selected={sel} aria-disabled={off || undefined}
                  className={`sp-opt${i === hi ? ' hi' : ''}${sel ? ' sel' : ''}${off ? ' off' : ''}`}
                  onMouseMove={() => { if (hi !== i) setHi(i); }} onClick={() => void choose(s)}
                >
                  <I className="sp-o-ico" />
                  <span className="sp-o-main">
                    <span className="sp-o-top"><span className="sp-o-name">{scopeName(s)}</span><span className="sp-o-mult">{fmtMult(s.multiplier)}</span></span>
                    <span className="sp-o-sub">{off ? kindText(s) : `${kindText(s)} · ${machines(s.machines)}`}</span>
                    {s.kind === 'community' && <span className="sp-o-note">{COMMUNITY_NOTICE}</span>}
                    {off && s.reason && <span className="sp-o-why">{s.reason}</span>}
                  </span>
                  {sel && <Check className="sp-o-check" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {variant === 'rail' && <span className="sp-hint">{hintOf(current)}</span>}
    </div>
  );
}
